import { ReactNode } from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/core';
import { Color } from '../../../../styles/Color';
import { ContentWrap, Css, ITheme, Responsive } from '../../../../ui';
import { getAirportLogo } from '../utils/getAirportLogo';
import { RouterLink } from '../../link/RouterLink';

export type NavBackgroundVariant = 'dark' | 'light';

interface IMainNavigationWrapperProps {
	siteSectionId?: string;
	backgroundVariant: NavBackgroundVariant;
	isSticky: boolean;
	justifyContent?: 'flex-start' | 'space-between';
	homeUrl?: string;
	children: ReactNode;
}

export const navBackgroundColors = {
	dark: Color.Neutral2,
	light: Color.Neutral1,
};

export function MainNavigationWrapper({
	isSticky,
	siteSectionId,
	backgroundVariant,
	justifyContent = 'flex-start',
	homeUrl,
	children,
}: IMainNavigationWrapperProps) {
	const logo = getAirportLogo(siteSectionId);

	return (
		<MainNavigationColumn
			isSticky={isSticky}
			backgroundVariant={backgroundVariant}
			className="main-navigation-column"
		>
			<StyledContentWrap justifyContent={justifyContent}>
				{homeUrl ? (
					<StyledLogoLink href={homeUrl} aria-label="Go to homepage">
						{logo}
					</StyledLogoLink>
				) : (
					<StyledLogo>{logo}</StyledLogo>
				)}
				{children}
			</StyledContentWrap>
		</MainNavigationColumn>
	);
}

const MainNavigationColumn = styled.div<{
	isSticky: boolean;
	backgroundVariant: NavBackgroundVariant;
}>`
	display: flex;
	align-items: center;
	background-color: ${({ isSticky, backgroundVariant }) =>
		isSticky ? Color.Neutral1 : navBackgroundColors[backgroundVariant]};
	width: 100%;
	padding: ${Css.pxToRem(16)} 0;

	${({ isSticky }) =>
		isSticky &&
		css`
			box-shadow: 0px 2px 2px 0px ${Color.BorderCard};
		`}
`;

const StyledContentWrap = styled(ContentWrap)<{
	justifyContent: IMainNavigationWrapperProps['justifyContent'];
}>`
	display: flex;
	width: 100%;
	align-items: center;
	gap: ${Css.pxToRem(24)};
	justify-content: ${({ justifyContent }) => justifyContent};
`;

const commonLogoStyles = ({ theme }: { theme: ITheme }) => css`
	text-decoration: none;

	${css`
		display: flex;

		svg,
		img {
			path {
				fill: ${theme.color.brand};
			}
			height: ${Css.pxToRem(44)};
		}

		.has,
		.spaceport {
			path {
				fill: currentColor;
			}
			height: ${Css.pxToRem(54)};
		}
	`}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		svg,
		img {
			width: ${Css.pxToRem(55)};
			height: 100%;
		}

		.spaceport {
			width: ${Css.pxToRem(110)};
			height: 100%;
		}
	}
`;

export const StyledLogoLink = styled(RouterLink, {
	shouldForwardProp: (props) => isPropValid(props),
})<{}, ITheme>`
	pointer-events: all;
	${commonLogoStyles}
`;

export const StyledLogo = styled.div<{}, ITheme>`
	${commonLogoStyles}
`;
